import Table from "common/Table/Table";
import { ReferredUser } from "types/affiliation.types";
import { ColumnsType } from "antd/es/table";
import {
  FROM_LAPTOP_COLUMN,
  FROM_TABLET_COLUMN,
} from "common/NewLook/responsiveColumns.ts";
import Text from "common/Typography/Text";
import {
  copyCodeOrLinkToClipboard,
  getReferralOrPromoCode,
} from "features/affiliation/common/utils";
import {
  AFFILIATION_ARTICLE_ID,
  USD_PRECISION,
  USERS_TAB_TO_REFERRAL_TYPE,
} from "features/affiliation/common/constants";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { toFixed } from "@shared/utils/numbers";
import { formatDate } from "@shared/utils/date";
import { AffiliationTabs } from "features/affiliation/common/types";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import { SMALL_SCREEN } from "common/constants";
import { copyCodeToClipboard, openMessage } from "utils/misc";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils";
import { useTheme } from "styled-components";
import ReferralEmptyState from "features/affiliation/common/ReferralEmptyState";
import { BlueCopyOutlined, CopyText } from "features/affiliation/common/styles";
import { checkSearch } from "@shared/utils/misc";
import Tooltip from "common/Tooltips/Tooltip";
import { CopyValue } from "features/affiliation/NewLook/parts";

import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  formatUsd,
  NUMBER_COLUMN,
} from "features/affiliation/NewLook/newLookUtils";
import {
  CellFigure,
  CellText,
  DateCell,
} from "features/affiliation/NewLook/TablePanel";

interface ReferredUsersTableProps {
  tab: AffiliationTabs.REFERRED_USERS | AffiliationTabs.EXISTING_USERS;
  referredUsers?: ReferredUser[];
  loading: boolean;
  searchFilter?: string;
}

const UsersTable = ({
  tab,
  referredUsers,
  loading,
  searchFilter,
}: ReferredUsersTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const appTheme = useTheme();
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );

  const referredUsersWithKey = referredUsers
    ?.filter((user) =>
      checkSearch(user.referral_codes.toString(), searchFilter)
    )
    .map((user, index) => {
      return { ...user, key: index };
    });

  const columns: ColumnsType<ReferredUser> = [
    {
      title:
        tab === AffiliationTabs.REFERRED_USERS
          ? t("users.registered_date")
          : t("users.paid_date"),
      dataIndex: "time",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      sorter: (a, b) => sortByNumberProperty(a, b, "time"),
      render: (time) => <Text>{formatDate(time, true, true)}</Text>,
    },
    {
      title: t("users.customer_id"),
      dataIndex: "customer_id",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "customer_id"),
      render: (customerId) => {
        const onCopy = () => {
          copyCodeToClipboard(customerId).catch(() => {});
          openMessage(getCopyMessage(t("users.customer_id")), "info");
        };
        if (appTheme.isNewLook)
          return <CopyValue onClick={onCopy}>{customerId}</CopyValue>;
        return (
          <CopyText onClick={onCopy}>
            <BlueCopyOutlined />
            <Text>{customerId}</Text>
          </CopyText>
        );
      },
    },
    {
      title:
        tab === AffiliationTabs.REFERRED_USERS ? (
          t("users.referral_link")
        ) : (
          <div style={{ display: "flex" }}>
            {t("users.promo_codes")}
            <HelperTooltip
              analyticsPageName={AnalyticsPageName.AFFILIATION_EXISTING_USERS}
              analyticsFieldName={t("users.promo_codes")}
              description={t("users.promo_codes_tooltip")}
              articleId={AFFILIATION_ARTICLE_ID}
            />
          </div>
        ),
      dataIndex: "referral_codes",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "referral_codes[0]"),
      render: (referralCodes) => {
        const referralOrPromoCode = getReferralOrPromoCode(
          referralCodes,
          USERS_TAB_TO_REFERRAL_TYPE[tab]
        );
        const onCopy = () =>
          copyCodeOrLinkToClipboard(
            referralOrPromoCode,
            tab === AffiliationTabs.REFERRED_USERS
          );
        if (appTheme.isNewLook)
          return (
            <Tooltip title={referralOrPromoCode}>
              <CopyValue
                onClick={onCopy}
                isLink={tab === AffiliationTabs.REFERRED_USERS}
                maxWidth={isSmallScreen ? 220 : 340}
              >
                {referralOrPromoCode}
              </CopyValue>
            </Tooltip>
          );
        return (
          <CopyText onClick={onCopy}>
            <BlueCopyOutlined />
            <Tooltip
              title={referralOrPromoCode}
              styles={{
                container: {
                  width: "fit-content",
                },
              }}
            >
              <Text
                $ellipsis={true}
                style={{
                  maxWidth: isSmallScreen ? 250 : 400,
                  width: "fit-content",
                  color: appTheme.colors.primaryBlue,
                }}
              >
                {referralOrPromoCode}
              </Text>
            </Tooltip>
          </CopyText>
        );
      },
    },
    {
      title: t("users.revenue"),
      dataIndex: "revenue",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "revenue"),
      render: (revenue) =>
        appTheme.isNewLook ? (
          formatUsd(revenue)
        ) : (
          <Text>${toFixed(revenue, USD_PRECISION)}</Text>
        ),
    },
    {
      title: t("users.earned"),
      dataIndex: "commission",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "commission"),
      render: (commission) =>
        appTheme.isNewLook ? (
          formatUsd(commission)
        ) : (
          <Text>${toFixed(commission, USD_PRECISION)}</Text>
        ),
    },
  ];

  // New look: the date over its time, amounts right-aligned.
  const newLookColumns: ColumnsType<ReferredUser> = [
    { ...columns[0], render: (time) => <DateCell time={time} /> },
    {
      ...columns[1],
      ...FROM_TABLET_COLUMN,
      render: (customerId) => (
        <CopyValue
          strong={true}
          onClick={() => {
            copyCodeToClipboard(customerId).catch(() => {});
            openMessage(getCopyMessage(t("users.customer_id")), "info");
          }}
        >
          {customerId}
        </CopyValue>
      ),
    },
    columns[2],
    {
      ...columns[3],
      ...NUMBER_COLUMN,
      ...FROM_LAPTOP_COLUMN,
      render: (revenue) => <CellText>{formatUsd(revenue)}</CellText>,
    },
    {
      ...columns[4],
      ...NUMBER_COLUMN,
      render: (commission) => <CellFigure>{formatUsd(commission)}</CellFigure>,
    },
  ];

  return (
    <Table<ReferredUser>
      columns={appTheme.isNewLook ? newLookColumns : columns}
      dataSource={referredUsersWithKey}
      loading={loading}
      locale={{
        emptyText: (
          <ReferralEmptyState tab={tab} isSearch={!!searchFilter?.length} />
        ),
      }}
    />
  );
};

export default UsersTable;
